import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Divider, Grid, Icon, Popup, Button } from "semantic-ui-react";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { openModal } from "../../../app/common/modals/modalSlice";
import {
  convertAddressFull,
  convertFullName,
  convertPhoneToLink,
} from "../../../app/common/util/util";
import { updatePartyInDb } from "../../../app/firestore/firestoreService";
import { toast } from "react-toastify";
import _ from "lodash";

export default function TransactionParties() {
  const dispatch = useDispatch();
  const { transaction, parties, transClients } = useSelector(
    (state) => state.transaction
  );
  const { people } = useSelector((state) => state.people);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { docsTransActive, docsTransArchived, docsTransShared } = useSelector(
    (state) => state.doc
  );
  const [viewMore, setViewMore] = useState(false);

  if (!transaction || !transClients)
    return <LoadingComponent content="Loading details..." />;

  // Function to check if a party's email was added by a non-user agent
  function isEmailAddedByAgent(party) {
    if (!docsTransActive?.docs || !party.email) return false;

    // Check all documents for signing details that match this party's role
    return docsTransActive.docs.some((doc) => {
      if (!doc.signingDetails) return false;

      const roleKey = _.camelCase(party.role); // Convert "Seller 2" to "seller2"
      const signingDetail = doc.signingDetails[roleKey];

      // Check if this signing detail was sent by an agent (non-user agent)
      return (
        signingDetail &&
        signingDetail.sentByAgent &&
        signingDetail.email === party.email
      );
    });
  }

  function handleEditClient(client) {
    dispatch(
      openModal({
        modalType: "TransactionClientEditForm",
        modalProps: {
          client: client,
          transaction: transaction,
        },
      })
    );
  }

  function handleAddClient(client) {
    dispatch(
      openModal({
        modalType: "TransactionClientAddForm",
        modalProps: {
          client: client,
          transaction: transaction,
          people: people,
        },
      })
    );
  }

  function handleEditParty(party) {
    dispatch(
      openModal({
        modalType: "TransactionPartyForm",
        modalProps: { party: party, transaction: transaction },
      })
    );
  }

  function handleRemoveLink(party) {
    try {
      updatePartyInDb(party, {
        isLinked: false,
        linkedTransactionId: "",
        linkedUserId: "",
        email: party.email,
        role: party.role,
      });
      toast.success("Link removed");
    } catch (error) {
      toast.error(error.message);
    }
  }

  function handleLinkNow(party) {
    dispatch(
      openModal({
        modalType: "TransactionLinkingModal",
        modalProps: { party: party, transaction: transaction },
      })
    );
  }

  function handleEyeIconClick(party) {
    const partyEmail = party.email;
    if (!partyEmail) return;
    if (!parties) return;

    // Combine all documents from different categories
    const allDocs = [
      ...(docsTransActive?.docs || []),
      ...(docsTransArchived?.docs || []),
      ...(docsTransShared?.docs || []),
    ];

    // Filter documents that are shared with this party
    const sharedDocs = allDocs.filter(
      (doc) => doc.sharingWith && doc.sharingWith.includes(partyEmail)
    );

    const docsForViewing = sharedDocs.filter(
      (doc) =>
        !doc.signingRequestedFor ||
        !doc.signingRequestedFor.includes(partyEmail)
    );

    // Filter documents for signing - only include if:
    // 1. The email is in signingRequestedFor, AND
    // 2. This specific party (by role) has been sent for signing but hasn't signed yet
    const docsForSigning = sharedDocs.filter((doc) => {
      if (!doc.signingRequestedFor || !doc.signingRequestedFor.includes(partyEmail)) {
        return false;
      }
      if (!doc.signingDetails) return false;

      // Check if this party's role has been sent for signing and hasn't signed yet
      const signingDetail = Object.values(doc.signingDetails).find(
        (detail) => detail.role === party.role && detail.sentOut === true
      );
      return signingDetail && signingDetail.signed !== true;
    });

    // Build shared email clients docs for signing
    const sharedEmailClientsDocsForSigning = [];

    // Check if this is a primary client with sharedEmailClients
    if (party.sharedEmailClients && party.sharedEmailClients.length > 0) {
      party.sharedEmailClients.forEach((sharedClient) => {
        // Look up the shared client in transClients (not parties, which is for non-client parties)
        const sharedClientParty = transClients?.find(
          (p) => p.id === sharedClient.odPartyId
        );
        if (sharedClientParty && sharedClientParty.email === partyEmail) {
          // Filter documents that are for signing for this shared client
          const docsForThisClient = sharedDocs.filter((doc) => {
            if (!doc.signingDetails) return false;
            const signingDetail = Object.values(doc.signingDetails).find(
              (detail) =>
                detail.role === sharedClientParty.role && detail.sentOut === true
            );
            return signingDetail && signingDetail.signed !== true;
          });
          if (docsForThisClient.length > 0) {
            sharedEmailClientsDocsForSigning.push({
              client: sharedClientParty,
              docs: docsForThisClient,
            });
          }
        }
      });
    }

    // Check if this is a secondary/tertiary client - find primary's documents
    const isSecondaryOrTertiary =
      party.role === "Buyer 2" ||
      party.role === "Buyer 3" ||
      party.role === "Seller 2" ||
      party.role === "Seller 3";

    if (isSecondaryOrTertiary) {
      const primaryRole = party.role.startsWith("Buyer") ? "Buyer" : "Seller";
      // Look up the primary client in transClients (not parties)
      const primaryParty = transClients?.find((p) => p.role === primaryRole);

      if (primaryParty && primaryParty.email === partyEmail) {
        // Filter documents for the primary client
        const docsForPrimary = sharedDocs.filter((doc) => {
          if (!doc.signingDetails) return false;
          const signingDetail = Object.values(doc.signingDetails).find(
            (detail) =>
              detail.role === primaryParty.role && detail.sentOut === true
          );
          return signingDetail && signingDetail.signed !== true;
        });

        if (docsForPrimary.length > 0) {
          sharedEmailClientsDocsForSigning.push({
            client: primaryParty,
            docs: docsForPrimary,
          });
        }
      }
    }

    // Always show the modal, even if there are no documents
    dispatch(
      openModal({
        modalType: "PartyDocumentPreviewModal",
        modalProps: {
          party: party,
          docsForViewing,
          docsForSigning,
          sharedEmailClientsDocsForSigning,
        },
      })
    );
  }
  return (
    <div className="secondary-page-wrapper">
      {transaction && (
        <>
          <Grid>
            {currentUserProfile?.userId !== transaction.agentProfile?.userId &&
              (transaction.agentRepresents === "Seller" ? (
                <Grid.Column width={16}>
                  <h4 className="tiny right margin tiny bottom margin">
                    Listing Agent:{" "}
                    {transaction.agentProfile?.firstName +
                      " " +
                      transaction.agentProfile?.lastName}
                      <span style={{color:"gray"}}>
                      {"  " + transaction.agentProfile?.email + ""}
                      {"  " + (transaction.agentProfile?.phone || "" )}
                      </span>
                  </h4>
                </Grid.Column>
              ) : (
                <Grid.Column width={16}>
                  <h4 className="tiny right margin tiny bottom margin">
                    Buyer Agent:{" "}
                    {transaction.agentProfile?.firstName +
                      " " +
                      transaction.agentProfile?.lastName}
                          <span style={{color:"gray"}}>

                      {" " + transaction.agentProfile?.email}
                       {"  " + (transaction.agentProfile?.phone || "" )}
                       </span>
                  </h4>
                </Grid.Column>
              ))}
            <Grid.Column width={16}>
              <div className="flex">
                <h3 className="tiny right margin tiny bottom margin">
                  My Client{transClients.length > 1 ? "s" : ""}
                </h3>
                {!transaction.clientThirdExists && (
                  <Icon
                    name="add circle"
                    color="blue"
                    link
                    onClick={() => handleAddClient()}
                    style={{ paddingTop: "5px" }}
                  />
                )}
              </div>
              <Divider className="mini top margin mini bottom margin" />
              <br />
              <Card.Group stackable>
                {transClients.map((client, index) => (
                  <Card key={client.role + index} className="tiny top margin">
                    <Card.Content extra>
                      <Icon
                        name="edit"
                        color="blue"
                        className="float-right"
                        style={{
                          cursor: "pointer",
                          marginRight: "5px",
                          marginLeft: "5px",
                        }}
                        link
                        onClick={() => handleEditClient(client)}
                      />
                      {client.email && !client.sharesEmailWithPrimary && (
                        <Popup
                          content="Preview what this party sees in their Client Portal Documents page"
                          trigger={
                            <Icon
                              name="eye"
                              color="blue"
                              className="float-right"
                              link
                              onClick={() => handleEyeIconClick(client)}
                              style={{
                                cursor: "pointer",
                                marginRight: "5px",
                                marginLeft: "5px",
                              }}
                            />
                          }
                          position="top center"
                          size="small"
                        />
                      )}
                      <Card.Header>{convertFullName(client)}</Card.Header>
                      {client.isTrust && <p>{client.entityName}</p>}
                      <p>{client.role}</p>
                      {client.hasPoa && client.poaFullName && (
                        <p>POA: {client.poaFullName}</p>
                      )}
                      <Card.Meta>
                        {client.address && client.address.street && (
                          <p>Address: {convertAddressFull(client.address)}</p>
                        )}
                      </Card.Meta>
                      {client.email && !isEmailAddedByAgent(client) && (
                        <div className="tiny bottom margin small top margin">
                          <Icon name="mail" />
                          <span className="tiny left margin">
                            <a href={`mailto:${client.email}`}>
                              {client.email}
                            </a>
                          </span>
                          <br />
                        </div>
                      )}
                      {client.phone && (
                        <div className="tiny bottom margin">
                          <Icon name="phone" />
                          <span className="tiny left margin">
                            <a href={`tel:${convertPhoneToLink(client.phone)}`}>
                              {client.phone}
                            </a>
                          </span>
                          <br />
                        </div>
                      )}
                      {client.fax && (
                        <div className="tiny bottom margin">
                          <Icon name="fax" />
                          <span className="tiny left margin">{client.fax}</span>
                          <br />
                        </div>
                      )}
                    </Card.Content>
                  </Card>
                ))}
              </Card.Group>
            </Grid.Column>

            <Grid.Column width={4}>
              <div className="flex">
                <h3 className="tiny right margin tiny bottom margin">
                  Other Parties
                </h3>
              <Popup
                flowing
                size="large"
                trigger={<Icon name="info circle" color="blue" style={{ marginLeft: "4px", marginRight: "20px", cursor: "pointer", paddingTop: "5px"}} />}
                // trigger={
                //   <Button basic size="mini" icon labelPosition="left">
                //     <Icon basic="true" name="info" />
                //     Info on adding Transaction Coordinators
                //   </Button>
                // }
              >
                <p className="bold text blue mini bottom margin">
                  Other Parties                
                  </p>
                <p className="text-dark text-normal small vertical margin">
                  Add other parties to this transaction, including the other agent and their assistant/TC and their clients, Title, Escrow, Lender, etc. by click on the blue plus sign icon <Icon
                  name="add circle"
                  color="blue"
                  link
                  style={{ paddingTop: "5px" }}
                />. 
                <br />Use "Other", "Other 2", "Other 3" for anyone you need to add that does not match to a specified role.
                </p>
                <p className="bold text blue mini bottom margin">
                  Handling both sides?
                </p>
                <p className="text-dark text-normal small vertical margin">
                 
                  Go to your Seller's transaction and click the "I am handling both sides" button to handle both sides to auto-create the parties and link.
                </p>
                <p className="bold text blue mini bottom margin">
                  How to Add Transaction Coordinators
                </p>
                <p className="text-dark text-normal small vertical margin">
                  <span className="bold">Your Transaction Coordinator: </span>
                  <br />
                  Add a new party and select the role "TC (Mine)". Your TC will
                  then have access to this transaction including all documents
                  and deadlines.
                </p>
                <p className="text-dark text-normal mini bottom margin">
                  <span className="bold">
                    The Other Agent's Transaction Coordinator:{" "}
                  </span>
                  <br />
                  When adding the other side's agent as a party, click the "This
                  party has a Transaction Coordinator" checkbox and add the TC's
                  information (including their email).
                  <br />
                  Their TC will have then have access to the documents you share
                  with their agent.
                </p>
              </Popup>
                <Icon
                  name="add circle"
                  color="blue"
                  link
                  onClick={() => handleEditParty()}
                  style={{ paddingTop: "5px" }}
                />
              </div>
              </Grid.Column>
              

            <Grid.Column width={10} >
                <div className="tiny right margin tiny bottom margin">
              {transaction.agentRepresents === "Buyer" &&
                // currentUserProfile.isBetaTester &&
                ((currentUserProfile.role === "agent" &&
                  currentUserProfile.id === transaction.userId) ||
                  currentUserProfile.role === "assistant") &&
                !parties?.some((p) => p.role === "Listing Agent") && (
                  <Popup
                    flowing
                    size="large"
                    trigger={
                      <Button basic size="mini" icon labelPosition="left">
                        <Icon basic="true" name="info" />
                        I am handling both
                        sides...
                      </Button>
                    }
                  >
                    <p className="bold text blue mini bottom margin">
                      How to Set Up as Handling Both Sides of a Transaction
                    </p>
                    <p className="text-dark text-normal small vertical margin">
                      <span className="bold"> </span>
                      <br />
                      1. Go to your <b>Seller's</b> transaction and into the Parties
                      section.
                      <br />
                      2. Click the "I am handling both sides..." button.
                      <br />
                      You will then be asked to select the Buyer's transaction.
                    </p>
                    <p className="text-dark text-normal mini bottom margin">
                      <span className="bold">Automation:</span>
                      <br />
                      This will:
                      <br />
                      - Add the Buyer Agent role to the Seller's transaction
                      <br />
                      - Add the Listing Agent role to the Buyer's transaction
                      <br />
                      - Add the Seller's client names to the Buyer's transaction
                      as parties
                      <br />
                      - Add the Buyer's client names to the Seller's transaction
                      as parties
                      <br />
                      - Link the two transactions together
                      <br />- Add the Title Company contact info to the Buyer's
                      transaction (if available)
                    </p>
                  </Popup>
                )}

              {transaction.agentRepresents === "Seller" &&
                // currentUserProfile.isBetaTester &&
                ((currentUserProfile.role === "agent" &&
                  currentUserProfile.id === transaction.userId) ||
                  currentUserProfile.role === "assistant") &&
                !parties?.some((p) => p.role === "Buyer Agent") && (
                  <Button
                    primary
                    size="mini"
                    icon
                    labelPosition="left"
                    onClick={() =>
                      dispatch(
                        openModal({
                          modalType: "DualAgentModal",
                          modalProps: { transaction: transaction },
                        })
                      )
                    }
                    style={{ marginLeft: "10px" }}
                  >
                    <Icon basic="true" name="handshake" />
                    {currentUserProfile.userId === transaction.userId
                      ? "I am "
                      : transaction.agentProfile?.firstName + " is "}
                    handling both sides...
                  </Button>
                )}
                              </div>
                </Grid.Column>
                
              <Grid.Column width={16}>
              <Divider className="tiny top margin mini bottom margin" />
              <br />
              <Card.Group stackable>
                {parties &&
                  parties.map((party, index) => (
                    <Card className="tiny top margin" key={party.role + index}>
                      <Card.Content extra>
                        <Icon
                          name="edit"
                          color="blue"
                          className="float-right"
                          link
                          onClick={() => handleEditParty(party)}
                        />
                        {/* {party.email && (
                          <Popup
                            content="Preview what this party sees in their Client Portal Documents page"
                            trigger={
                              <Icon
                                name="eye"
                                color="blue"
                                className="float-right"
                                link
                                onClick={() => handleEyeIconClick(party)}
                                style={{
                                  cursor: "pointer",
                                  marginRight: "8px",
                                }}
                              />
                            }
                            position="top center"
                            size="small"
                          />
                        )} */}
                        <Card.Header>{convertFullName(party)}</Card.Header>

                        {party.isTrust && <p>{party.entityName}</p>}
                        <p>{party.role}</p>
                        {party.isLinked && (
                          <div className="box-blue tiny padding small bottom margin">
                            <p className="zero bottom margin text blue">
                              Transaction is Linked to this Agent &nbsp;&nbsp;
                              <Icon
                                name="trash"
                                color="blue"
                                link
                                onClick={() => handleRemoveLink(party)}
                              />
                            </p>
                          </div>
                        )}
                        {!party.isLinked &&
                          party.isUser &&
                          (party.role === "Listing Agent" ||
                            party.role === "Buyer Agent") && (
                            <p>
                              <i>
                                This party is a TransActioner User.
                                <br />
                                You should be able to link transactions.
                                <Popup
                                  flowing
                                  size="small"
                                  trigger={
                                    <Icon
                                      name="info"
                                      color="blue"
                                      circular
                                      inverted
                                      size="mini"
                                      style={{
                                        marginLeft: "3px",
                                        marginBottom: "3px",
                                      }}
                                    />
                                  }
                                >
                                  <p className="bold text blue mini bottom margin">
                                    Linking Transactions
                                  </p>
                                  <p className="text-dark text-normal small vertical margin">
                                    In order to link, you will need to set up
                                    the MLS # and/or legal description in the
                                    transaction
                                    <br /> and the other agent will also need
                                    this information set up.
                                    <br /> &nbsp; The first time you share a
                                    document with this agent, you will be
                                    prompted to link the transaction.
                                    <br />
                                    When you link transactions, documents you
                                    share with this agent will automatically
                                    show up in their transaction.
                                  </p>
                                </Popup>
                              </i>
                              <br />
                              <Button
                                size="mini"
                                color="blue"
                                onClick={() => handleLinkNow(party)}
                                style={{ marginTop: "5px" }}
                              >
                                Link Now
                              </Button>
                            </p>
                          )}
                        <Card.Meta>
                          {party.companyName && (
                            <p>Company: {party.companyName}</p>
                          )}
                        </Card.Meta>
                        <Card.Meta>
                          {party.address && party.address.street && (
                            <p>Address: {convertAddressFull(party.address)}</p>
                          )}
                        </Card.Meta>
                        {(party.brokerLicenseNumber ||
                          party.brokerageName ||
                          party.brokerageId ||
                          party.brokerageLicenseNumber) && (
                          <>
                            <p
                              style={{ color: "#0074cc", cursor: "pointer" }}
                              onClick={() => setViewMore(!viewMore)}
                            >
                              More Details
                            </p>
                            {viewMore && (
                              <>
                                <Card.Meta>
                                  {party.brokerLicenseNumber && (
                                    <p>
                                      Agent License #:{" "}
                                      {party.brokerLicenseNumber}
                                    </p>
                                  )}
                                </Card.Meta>
                                <Card.Meta>
                                  {party.brokerageName && (
                                    <p>Brokerage: {party.brokerageName}</p>
                                  )}
                                </Card.Meta>
                                <Card.Meta>
                                  {party.brokerageId && (
                                    <p>Brokerage ID: {party.brokerageId}</p>
                                  )}
                                </Card.Meta>
                                <Card.Meta>
                                  {party.brokerageLicenseNumber && (
                                    <p>
                                      Brokerage Licence #:{" "}
                                      {party.brokerageLicenseNumber}
                                    </p>
                                  )}
                                </Card.Meta>
                              </>
                            )}
                          </>
                        )}

                        {party.email && !isEmailAddedByAgent(party) && (
                          <div className="small top margin">
                            <Card.Meta>
                              <div className="tiny bottom margin">
                                <Icon name="mail" />
                                <span className="tiny left margin">
                                  <a href={`mailto:${party.email}`}>
                                    {party.email}
                                  </a>
                                </span>
                                <br />
                              </div>
                            </Card.Meta>
                          </div>
                        )}
                        {party.phone && (
                          <Card.Meta>
                            <div className="tiny bottom margin">
                              <Icon name="phone" />
                              <span className="tiny left margin">
                                <a
                                  href={`tel:${convertPhoneToLink(
                                    party.phone
                                  )}`}
                                >
                                  {party.phone}
                                </a>
                              </span>
                              <br />
                            </div>
                          </Card.Meta>
                        )}
                        {party.fax && (
                          <Card.Meta>
                            <div className="tiny bottom margin">
                              <Icon name="fax" />
                              <span className="tiny left margin">
                                {party.fax}
                              </span>
                              <br />
                            </div>
                          </Card.Meta>
                        )}
                      </Card.Content>
                      {party.hasAssistant && (
                        <>
                          <Card.Content extra>
                            <h4 className="tiny bottom margin">Assistant</h4>
                            <p>
                              {party.assistant?.firstName}{" "}
                              {party.assistant?.lastName}
                            </p>
                            {party.assistant?.email && (
                              <div className="tiny bottom margin">
                                <Icon name="mail" />
                                <span className="tiny left margin">
                                  <a href={`mailto:${party.assistant?.email}`}>
                                    {party.assistant?.email}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                            {party.assistant?.phone && (
                              <div className="tiny bottom margin">
                                <Icon name="phone" />
                                <span className="tiny left margin">
                                  <a
                                    href={`tel:${convertPhoneToLink(
                                      party.assistant?.phone
                                    )}`}
                                  >
                                    {party.assistant?.phone}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                          </Card.Content>
                        </>
                      )}
                      {party.hasCoAgent && (
                        <>
                          <Card.Content extra>
                            <h4 className="tiny bottom margin">CoAgent</h4>
                            <p>
                              {party.coAgent?.firstName}{" "}
                              {party.coAgent?.lastName}
                            </p>
                            {party.coAgent?.email && (
                              <div className="tiny bottom margin">
                                <Icon name="mail" />
                                <span className="tiny left margin">
                                  <a href={`mailto:${party.coAgent?.email}`}>
                                    {party.coAgent?.email}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                            {party.coAgent?.phone && (
                              <div className="tiny bottom margin">
                                <Icon name="phone" />
                                <span className="tiny left margin">
                                  <a
                                    href={`tel:${convertPhoneToLink(
                                      party.coAgent?.phone
                                    )}`}
                                  >
                                    {party.coAgent?.phone}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                          </Card.Content>
                        </>
                      )}
                      {party.hasTransactionCoordinator && (
                        <>
                          <Card.Content extra>
                            <h4 className="tiny bottom margin">
                              Transaction Coordinator
                            </h4>
                            <p>
                              {party.transactionCoordinator?.firstName}{" "}
                              {party.transactionCoordinator?.lastName}
                            </p>
                            {party.transactionCoordinator?.email && (
                              <div className="tiny bottom margin">
                                <Icon name="mail" />
                                <span className="tiny left margin">
                                  <a
                                    href={`mailto:${party.transactionCoordinator?.email}`}
                                  >
                                    {party.transactionCoordinator?.email}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                            {party.transactionCoordinator?.phone && (
                              <div className="tiny bottom margin">
                                <Icon name="phone" />
                                <span className="tiny left margin">
                                  <a
                                    href={`tel:${convertPhoneToLink(
                                      party.transactionCoordinator?.phone
                                    )}`}
                                  >
                                    {party.transactionCoordinator?.phone}
                                  </a>
                                </span>
                                <br />
                              </div>
                            )}
                          </Card.Content>
                        </>
                      )}
                    </Card>
                  ))}
              </Card.Group>
            </Grid.Column>
          </Grid>
        </>
      )}
    </div>
  );
}
